import React, { useState, useEffect } from "react";
import moment from "moment";
import styles from "../../../styles.module.scss";
import { Checkbox, DatePicker, Modal } from "antd";
import classNames from "classnames";
import Form, { InputField, NewSelectField, RadioField } from "@components/form";
import { Flex } from "@components/ui/Flex";
import { Text } from "@components/ui/Text";
import { ColumnField, modeOptionsIp21, ParameterField, parameterOptionsIp21Type, realTimeOptions, requestTypeOptions, summaryTypeOptions, calculationBasisOptions, summaryTimeTypeOptions, summaryIntervalUnitOptions } from "../constant";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { TooltipTop } from "@components/tooltips";
import { BsInfoCircle, BsPlusLg } from "react-icons/bs";
import { timezoneOptions } from "@pages/workflow-page/modals/schedule-workflow";
import { EditableTagTable } from "./SelectedTagTable";
import { CrossIcon } from "@components/formcreators/icons";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import TabsComponent from "@components/tabs";
import CustomMonacoEditor from "@components/monacoEditor";
import { isEmpty } from "lodash";
import { InformativeIcon } from "@pages/workflow-page/assets/icons";

interface HierarchyFormProps {
    activeStep: number;
    setActiveStep: (step: number) => void;
    initialValues: any;
    formSchema: any;
    tagPosition: string;
    selectedRowKeys: string[];
    setShowForm: (show: boolean) => void;
    handleOnSubmit: (values: any) => void;
    isEditMode?: boolean;
    isRDBMSview?: boolean;
    isAspenIp21View?: boolean;
    editorControllerPropsIp21Aspen?: any;
}

const HierarchyReqForm: React.FC<HierarchyFormProps> = ({
    activeStep,
    setActiveStep,
    initialValues,
    formSchema,
    setShowForm,
    selectedRowKeys,
    tagPosition,
    handleOnSubmit,
    isEditMode = false,
    isRDBMSview = false,
    isAspenIp21View = false,
    editorControllerPropsIp21Aspen
}) => {
    const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
    const [attributesMode, setAttributesMode] = useState<'table' | 'manual' | 'csv'>(initialValues?.isAttributesManualEntry ? 'manual' : 'table');

    useEffect(() => {
        setAttributesMode(initialValues?.isAttributesManualEntry ? 'manual' : 'table');
    }, [initialValues?.isAttributesManualEntry]);
    const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') e.preventDefault();
    };
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [csvRows, setCsvRows] = useState<string[][]>([]);
    const [csvFileName, setCsvFileName] = useState<string>('');
    // const selectedTags = initialValues.tags.split(",");
    const formatString = "MM/DD/YYYY HH:mm:ss";

    const getDisplayValue = (dateStr: string | null, timezone: string) => {
        if (!dateStr) return null;
        const date = moment(dateStr);
        return timezone === "utc" ? date.utc() : date.local();
    };

    const [columns, setColumns] = useState<ColumnField[]>(() => {
        const initialCols = initialValues?.columns && Array.isArray(initialValues.columns)
            ? initialValues.columns.filter((col: string): col is string => typeof col === 'string')
            : [];

        return initialCols.length > 0
            ? initialCols.map((col: string, index: number) => ({
                id: `col-${index}`,
                value: col
            }))
            : [];
    });

    const [parameters, setParameters] = useState<ParameterField[]>(() => {
        const initialCols = initialValues?.parameters && Array.isArray(initialValues.parameters)
            ? initialValues.parameters.filter((col: ParameterField): col is ParameterField => typeof col === 'object')
            : [];

        return initialCols.length > 0
            ? initialCols.map((col: ParameterField, index: number) => ({
                id: `col-${index}`,
                type: col.type,
                value: (col as any).value || '',
                name: (col as any).name || `change_parameter${index + 1}`,
                initialValue: (col as any).initialValue || '',
                incrementalValue: (col as any).incrementalValue || null
            }))
            : [];
    });

    useEffect(() => {
        if (!isAspenIp21View || !isRDBMSview) return;
        const incomingParams: ParameterField[] = (Array.isArray(initialValues?.parameters) ? initialValues.parameters : [])
            .filter((col: ParameterField): col is ParameterField => typeof col === 'object')
            .map((col: any, index: number) => ({
                id: `col-${index}`,
                type: (initialValues?.parameterType && (initialValues.parameterType as any)[index]) || col?.type || 'single',
                value: col?.value || '',
                name: col?.name || `change_parameter${index + 1}`,
                initialValue: col?.initialValue || '',
                incrementalValue: col?.incrementalValue ?? null,
            }));
        setParameters(incomingParams);
    }, [isAspenIp21View || isRDBMSview, initialValues?.parameters, initialValues?.parameterType]);

    const updateColumn = (id: string, value: string) => {
        if (!id || value === undefined) return;
        // if (isRDBMSview) {
        //     setColumns(columns.map(col =>
        //         col.id === id ? { ...col, value: value || '' } : col
        //     ));
        // }
        if (isAspenIp21View || isRDBMSview) {
            setParameters(parameters.map(par =>
                par.id === id ? { ...par, value: value || '' } : par
            ));
        }
    };

    return (
        <>
            <Form
                initialValues={initialValues}
                onSubmit={handleOnSubmit}
                validationSchema={formSchema}
                enableReinitialize
            >
                {({ _formikprops: { values, errors, touched, setFieldValue } }) => {
                    const addColumn = () => {
                        // if (isRDBMSview) {
                        //     setColumns([...columns, { id: Date.now().toString(), value: '' }]);
                        // }
                        if (isAspenIp21View || isRDBMSview) {
                            const idx = parameters.length;
                            const newParam = { id: Date.now().toString(), type: 'single', value: '', name: `change_parameter${idx + 1}`, initialValue: '', incrementalValue: null };
                            const newParameters = [...parameters, newParam];
                            setParameters(newParameters);
                            const newParameterTypes = { ...values.parameterType };
                            newParameterTypes[newParameters.length - 1] = 'single';
                            setFieldValue('parameterType', newParameterTypes);
                            setFieldValue('parameters', newParameters);
                            setFieldValue(`parameterValues.${idx}.increment`, 1);
                        }
                    };

                    const removeColumn = (id: string) => {
                        if (columns.length > 0 && isRDBMSview || isAspenIp21View) {
                            setColumns(columns.filter(col => col.id !== id));
                        }
                        if (parameters.length > 0 && (isAspenIp21View || isRDBMSview)) {
                            const updatedParameters = parameters.filter(par => par.id !== id);
                            setParameters(updatedParameters);
                            const newParameterTypes: Record<number, string> = {};
                            updatedParameters.forEach((param, index) => {
                                newParameterTypes[index] = param.type || 'single';
                            });
                            setFieldValue('parameterType', newParameterTypes);
                            setFieldValue('parameters', updatedParameters);
                        }
                    };
                    const updateParameterType = (id: string, newType: string) => {
                        if (!id || !newType) return;
                        if (isAspenIp21View || isRDBMSview) {
                            setParameters(parameters.map(par =>
                                par.id === id ? { ...par, type: newType } : par
                            ));
                            const index = parameters.findIndex(p => p.id === id);
                            if (index >= 0) {
                                setFieldValue(`parameterType.${index}`, newType);
                            }
                        }
                    };

                    const handleCopyText = (text: string) => {
                        navigator.clipboard.writeText("{" + text + "}")
                            .then(() => {
                                successAlert('Copied To Clipboard');
                            })
                            .catch(() => {
                                errorAlert('Failed To Copy Text');
                            });
                    };

                    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.name.toLowerCase().endsWith('.csv')) {
                            errorAlert('Only CSV files are allowed. Please select a .csv file.');
                            e.target.value = '';
                            return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => {
                            const text = String(reader.result || '');
                            const rows = text
                                .split(/\r?\n/)
                                .filter((r) => r.trim())
                                .map((row) =>
                                    row.split(',').map((c) => c.trim().replace(/^"|"$/g, ''))
                                );
                            if (rows.length < 2) {
                                errorAlert('CSV appears to be empty or missing data');
                                return;
                            }
                            setCsvHeaders(rows[0]);
                            setCsvRows(rows.slice(1));
                            setCsvFileName(file.name);
                            successAlert(`Parsed ${rows.slice(1).length} row(s). Select a column to extract tags.`);
                        };
                        reader.onerror = () => errorAlert('Failed to read CSV file');
                        reader.readAsText(file);
                        e.target.value = '';
                    };

                    const handleCsvColumnSelect = (columnName: string) => {
                        const colIndex = csvHeaders.indexOf(columnName);
                        if (colIndex < 0) return;
                        const tags = csvRows
                            .map((row) => row[colIndex] || '')
                            .filter((t) => t)
                            .join(', ');
                        if (!tags) {
                            errorAlert(`No values found in "${columnName}" column`);
                            return;
                        }
                        setFieldValue('tags', tags);
                        successAlert(`${tags.split(',').filter(Boolean).length} tag(s) loaded from "${columnName}"`);
                    };

                    return (
                        <>
                            <div className={classNames(styles.jsonHierarchyForm, styles.hierarchyForm)}>
                                <ShowWhenTrue show={!isRDBMSview && !isAspenIp21View}>
                                    <div style={{ padding: 16 }}>
                                        <InputField
                                            name="requestName"
                                            label="Name"
                                            autoComplete="off"
                                            required={true}
                                            disabled={isEditMode}
                                            className={styles["requestName__field"]}
                                        />
                                        <label className="inputfield__label"><span className={'red-star'}>Attributes</span></label>
                                        <div className={styles["requestName__field"]}>
                                            <TabsComponent
                                                activeKey={attributesMode}
                                                onTabChange={(key) => {
                                                    setAttributesMode(key as any);
                                                    setFieldValue('isAttributesManualEntry', key === 'manual');
                                                }}
                                                className={styles["attributes_tabs"]}
                                                size="small"
                                                items={[
                                                    {
                                                        key: 'table',
                                                        label: 'Select from Attributes',
                                                        children: (
                                                            <>
                                                                <Text fontSize={12} color="#fff" className={styles["attributes_help_text"]}>
                                                                    Select tags from the attributes table. Use the search box to filter and the checkboxes to select.
                                                                </Text>
                                                                <div className={styles["tag_container"]}>
                                                                    <EditableTagTable
                                                                        setFieldValue={setFieldValue}
                                                                        selectedRowKeys={selectedRowKeys}
                                                                        tagPosition={tagPosition}
                                                                        tags={values.tags}
                                                                    />
                                                                </div>
                                                            </>
                                                        ),
                                                    },
                                                    {
                                                        key: 'manual',
                                                        label: 'Manual / CSV',
                                                        children: (
                                                            <>
                                                                <Text fontSize={12} color="#fff" className={styles["attributes_help_text"]}>
                                                                    Type PI tags as a comma-separated list, or upload a CSV and pick a column to fill the field automatically.
                                                                </Text>
                                                                <div className={styles["requestName__field"]}>
                                                                    <textarea
                                                                        className={styles["manual_tags_input"]}
                                                                        value={values.tags || ''}
                                                                        onChange={(e) => setFieldValue('tags', e.target.value)}
                                                                        placeholder="Enter PI tags separated by commas"
                                                                        rows={3}
                                                                    />
                                                                </div>
                                                                <div className={classNames(styles["requestName__field"], styles["csv_upload_field"])}>
                                                                    <Flex alignItems="center" gap="10px">
                                                                        <label className={styles["csv_upload_box"]}>
                                                                            <input
                                                                                type="file"
                                                                                accept=".csv"
                                                                                onChange={handleCsvUpload}
                                                                                className={styles["csv_file_input"]}
                                                                            />
                                                                            <span>{csvFileName || 'Choose CSV file'}</span>
                                                                        </label>
                                                                        <ShowWhenTrue show={csvHeaders.length > 0}>
                                                                            <NewSelectField
                                                                                name="csv_tag_column"
                                                                                options={csvHeaders.map((h) => ({ label: h, value: h }))}
                                                                                placeholder="Pick tag column"
                                                                                onOptionClick={(option) => handleCsvColumnSelect(String(option.value))}
                                                                                className={styles["csv_column_select"]}
                                                                            />
                                                                        </ShowWhenTrue>
                                                                    </Flex>
                                                                </div>
                                                            </>
                                                        ),
                                                    },
                                                ]}
                                            />
                                        </div>
                                        <div className={styles["requestName__field"]}>
                                            {!!touched.tags && <Text color={"#ff7761"} fontSize={10}>{errors.tags}</Text>}
                                        </div>
                                        <ShowWhenTrue show={tagPosition === "Elements"}>
                                            <InputField
                                                name="elementRootPath"
                                                label="Element Root"
                                                autoComplete="off"
                                                className={styles["requestName__field"]}
                                            />
                                            <InputField
                                                name="elementPath"
                                                label="Element"
                                                autoComplete="off"
                                                className={styles["requestName__field"]}
                                            />
                                        </ShowWhenTrue>
                                        <Flex alignItems={"center"} className={classNames(styles["checkbox-tag"], styles["requestName__field"])}>
                                            <Checkbox
                                                name="all_descendants"
                                                onChange={(e) => setFieldValue("all_descendants", e.target.checked)}
                                                checked={values.all_descendants}
                                            >
                                                Include Sub-Elements
                                            </Checkbox>
                                            <TooltipTop overlay={"Includes this element and all nested elements below it in the hierarchy."}><div><BsInfoCircle /></div></TooltipTop>
                                        </Flex>
                                        {/* <NewSelectField
                                        name="output_format"
                                        label={"Output Format"}
                                        options={outputFormatOptions}
                                        className={styles["requestName__field"]}
                                    /> */}
                                    <Flex alignItems="center" gap="8px" className={styles["info_note"]}>
                                        <InformativeIcon />
                                        <Text fontSize={12} fontWeight={400}>Once saved, the Ingestion Mode cannot be changed.</Text>
                                    </Flex>
                                        <NewSelectField
                                            name="requestType"
                                            label={<Flex gap="6px">Ingestion Mode <span className="red-star" /><TooltipTop overlay={`Choose how data is retrieved from the PI System:
                                    • Raw fetches actual stored values
                                    • Interpolated fills-in values at regular intervals
                                    • Real-time streams current values as they change
                                    `}><div><BsInfoCircle /></div></TooltipTop></Flex>}
                                            options={requestTypeOptions}
                                            // required={true}
                                            className={styles["requestName__field"]}
                                            disabled={isEditMode}
                                        />
                                        <ShowWhenTrue show={values.requestType === "real_time"}>
                                            <RadioField
                                                name="realTimeValues"
                                                options={realTimeOptions}
                                                // label='Interpolated Values'
                                                required
                                                className={styles["requestName__field"]}
                                                readOnly={isEditMode}
                                            />
                                            <ShowWhenTrue show={values.realTimeValues === "live"}>
                                                <Flex gap="20px" className={styles["field_row"]}>
                                                    <InputField
                                                        name="period_ms"
                                                        label="Period (in ms)"
                                                        autoComplete="off"
                                                        required={true}
                                                        className={styles["requestName__field"]}
                                                    />
                                                    <InputField
                                                        name="refresh_period_ms"
                                                        label="Refresh Period (in ms)"
                                                        autoComplete="off"
                                                        required={true}
                                                        className={styles["requestName__field"]}
                                                    />
                                                </Flex>
                                            </ShowWhenTrue>
                                        </ShowWhenTrue>

                                        <ShowWhenTrue show={values.requestType === "interpolated"}>
                                            <div>
                                                <label className="inputfield__label"><span className="red-star">Sample Values Every</span></label>
                                                <Flex gap="20px" className={styles["field_row"]}>
                                                    <NewSelectField
                                                        name="frequency"
                                                        options={Array.from({ length: 60 }, (_, i) => i + 1).map((i) => ({ label: i.toString(), value: i.toString() }))}
                                                        className={styles["requestName__field"]}
                                                    />
                                                    <NewSelectField
                                                        name="frequencyType"
                                                        options={[{ label: 'Seconds', value: "seconds" }, { label: "Minutes", value: "minutes" }, { label: "Hours", value: "hours" }]}
                                                        className={styles["requestName__field"]}
                                                    />
                                                </Flex>
                                            </div>
                                        </ShowWhenTrue>
                                        <ShowWhenTrue show={values.requestType === "summary"}>
                                            <div>
                                                <label className="inputfield__label">Summary Interval Timespan</label>
                                                <Flex gap="20px" className={styles["field_row"]}>
                                                    <InputField
                                                        name="summary_interval_value"
                                                        type="number"
                                                        min={0}
                                                        onKeyDown={blockNegativeKeys}
                                                        autoComplete="off"
                                                        className={styles["requestName__field"]}
                                                    />
                                                    <NewSelectField
                                                        name="summary_interval_unit"
                                                        options={summaryIntervalUnitOptions}
                                                        className={styles["requestName__field"]}
                                                    />
                                                </Flex>
                                            </div>
                                            <Flex gap="20px" className={styles["field_row"]}>
                                                <NewSelectField
                                                    name="summary_types"
                                                    label="Summary Types"
                                                    options={summaryTypeOptions}
                                                    multiple_select
                                                    required
                                                    className={styles["requestName__field"]}
                                                />
                                                <NewSelectField
                                                    name="calculation_basis"
                                                    label="Calculation Basis"
                                                    options={calculationBasisOptions}
                                                    className={styles["requestName__field"]}
                                                />
                                            </Flex>
                                            <Flex gap="20px" className={styles["field_row"]}>
                                                <NewSelectField
                                                    name="summary_time_type"
                                                    label="Summary Time Type"
                                                    options={summaryTimeTypeOptions}
                                                    className={styles["requestName__field"]}
                                                />
                                                <InputField
                                                    name="page_size"
                                                    label="Page Size"
                                                    type="number"
                                                    min={0}
                                                    onKeyDown={blockNegativeKeys}
                                                    autoComplete="off"
                                                    className={styles["requestName__field"]}
                                                />
                                                <InputField
                                                    name="chunking_num_tags"
                                                    label="Chunking Num Tags"
                                                    type="number"
                                                    min={0}
                                                    onKeyDown={blockNegativeKeys}
                                                    autoComplete="off"
                                                    className={styles["requestName__field"]}
                                                />
                                            </Flex>
                                        </ShowWhenTrue>
                                        <ShowWhenTrue show={values.requestType === "raw" || values.requestType === "interpolated"}>
                                            <div className={styles["field_half"]}>
                                                <InputField
                                                    name="chunking_num_tags"
                                                    type="number"
                                                    label="Chunking Num Tags"
                                                    min={0}
                                                    onKeyDown={blockNegativeKeys}
                                                    autoComplete="off"
                                                    className={styles["requestName__field"]}
                                                />
                                            </div>
                                        </ShowWhenTrue>
                                        <ShowWhenTrue show={values.requestType != "real_time"}>
                                            <Flex alignItems="center" gap="8px" className={styles["info_note"]}>
                                                <InformativeIcon />
                                                <Text fontSize={12}>Start and End Time fields won’t update after the scheduled run.</Text>
                                            </Flex>
                                            <div className="split__fields--tripleHalfwide">
                                                <Flex className={styles["requestName__field"]} display="flex" flexDirection="column" justifyContent="space-between">
                                                    <label className="inputfield__label">Start Time <span className="red-star" /></label>
                                                    <DatePicker
                                                        name="start_time_str"
                                                        showTime
                                                        format={formatString}
                                                        value={getDisplayValue(values.start_time_str, values.activeTimezone)}
                                                        disabledDate={(current) =>
                                                            !!current && current > moment()
                                                        }
                                                        onChange={(date) => {
                                                            setFieldValue("start_time_str", date?.toISOString() || null);
                                                            if (
                                                                date &&
                                                                values.end_time_str &&
                                                                moment(values.end_time_str).isBefore(date)
                                                            ) {
                                                                setFieldValue("end_time_str", null);
                                                            }
                                                        }}
                                                    />
                                                </Flex>

                                                <Flex display="flex" flexDirection="column" justifyContent="space-between">
                                                    <label className="inputfield__label">End Time <span className="red-star" /></label>
                                                    <DatePicker
                                                        className={styles["requestName__field"]}
                                                        name="end_time_str"
                                                        showTime
                                                        format={formatString}
                                                        value={getDisplayValue(values.end_time_str, values.activeTimezone)}
                                                        disabledDate={(current) =>
                                                            !!current &&
                                                            (current <= moment(values.start_time_str) || current >= moment())
                                                        }
                                                        onChange={(date) => {
                                                            setFieldValue("end_time_str", date?.toISOString() || null);
                                                        }}
                                                    />
                                                </Flex>
                                                <Flex display="flex" flexDirection="column" justifyContent="space-between">
                                                    <NewSelectField
                                                        options={timezoneOptions}
                                                        name="activeTimezone"
                                                        label="Time Zone"
                                                        className={styles["requestName__field"]}
                                                    />
                                                </Flex>
                                            </div>
                                        </ShowWhenTrue>
                                        <div className="split__fields--halfwide" style={{ marginTop: 8, width: '64%' }}>
                                            {!!touched.start_time_str && <Text color={"#ff7761"} fontSize={10}>{errors.start_time_str}</Text>}
                                            {!!touched.end_time_str && <Text color={"#ff7761"} fontSize={10}>{errors?.end_time_str}</Text>}
                                        </div>
                                    </div>
                                </ShowWhenTrue>
                                {/* <ShowWhenTrue show={isRDBMSview}>
                                    <div style={{ padding: 16 }}>
                                        <InputField
                                            name="requestName"
                                            label="Name"
                                            autoComplete="off"
                                            required={true}
                                            disabled={isEditMode}
                                            className={styles["requestName__field"]}
                                        />
                                    </div>
                                    <div className={styles.columnFieldsContainer}>
                                        <div className={styles.columnHeader}>
                                            <label className="inputfield__label">Change Columns</label>
                                            <button
                                                id="Btn_NewColumn"
                                                type="button"
                                                className="btn-sm btn-yellow-transparent btn__new__workflow btn__new__action"
                                                onClick={addColumn}
                                                style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}
                                            >
                                                <BsPlusLg />
                                                <span>Column</span>
                                            </button>
                                        </div>
                                        <div className={styles.columnsList}>
                                            {columns?.map((column, index) => (
                                                <div key={column.id} className={styles.columnInputGroup}>
                                                    <div className={styles.columnInput}>
                                                        <InputField
                                                            name={`columns.${index}`}
                                                            placeholder={`Column ${index + 1}`}
                                                            value={column?.value || ''}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement> | string) => {
                                                                const value = typeof e === 'string' ? e : e?.target?.value || '';
                                                                updateColumn(column.id, value);
                                                            }}
                                                            autoComplete="off"
                                                        />
                                                    </div>
                                                    {columns.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className={styles.removeColumnButton}
                                                            onClick={() => removeColumn(column.id)}
                                                            title={`Remove column ${index + 1}`}
                                                        >
                                                            <CrossIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </ShowWhenTrue> */}
                                <ShowWhenTrue show={isAspenIp21View || isRDBMSview}>
                                    <div style={{ padding: 16 }}>
                                        <InputField
                                            name="requestName"
                                            label="Name"
                                            autoComplete="off"
                                            required={true}
                                            disabled={isEditMode}
                                            className={styles["requestName__field"]}
                                        />
                                        <Flex alignItems="center" gap="8px" className={styles["info_note"]}>
                                            <InformativeIcon />
                                            <Text fontSize={12} fontWeight={400}>Once saved, the schedule type cannot be changed.</Text>
                                        </Flex>
                                        <RadioField
                                            name="mode"
                                            options={modeOptionsIp21}
                                            label='Mode'
                                            inline
                                            required
                                            className={styles["requestName__field"]}
                                            readOnly={isEditMode}
                                        />
                                        <ShowWhenTrue show={values.mode === "recurring"}>
                                            <div className={styles.columnFieldsContainer}>
                                                <CustomMonacoEditor
                                                    {...editorControllerPropsIp21Aspen}
                                                    editorHeight="200px"
                                                    editorWidth="100%"
                                                    scrollBeyondLastLine
                                                    language="sql"

                                                    setShowModal={(value, show) => {
                                                        editorControllerPropsIp21Aspen?.setEditorValue(value);
                                                        setIsVisibleModal(show);
                                                    }}
                                                />
                                                <div className={styles.columnHeader}>
                                                    <label className="inputfield__label">Change Parameters</label>
                                                    <button
                                                        id="Btn_NewColumn"
                                                        type="button"
                                                        className="btn-sm btn-yellow-transparent btn__new__workflow btn__new__action"
                                                        onClick={addColumn}
                                                        style={{ display: 'flex', alignItems: 'center' }}
                                                    >
                                                        <BsPlusLg />
                                                        <span>Parameter</span>
                                                    </button>
                                                </div>
                                                <ShowWhenTrue show={!isEmpty(parameters)}>
                                                    <div className={styles.columnsList}>
                                                        <div className={styles.gridTableHeader}>
                                                            <Text style={{ textAlign: 'center' }}>Type</Text>
                                                            <Text style={{ textAlign: 'center' }}>Parameter Name</Text>

                                                        </div>
                                                        <div style={{ maxHeight: '300px', overflowY: 'scroll' }}>
                                                            {parameters?.map((parameter, index) => (
                                                                <div key={parameter.id} className={styles.gridTableItemList}>
                                                                    <Flex justifyContent="center" alignItems="center">
                                                                        <RadioField
                                                                            name={`parameterType.${index}`}
                                                                            options={parameterOptionsIp21Type}
                                                                            inline
                                                                            required
                                                                            onChange={(value: string) => {
                                                                                updateParameterType(parameter.id, value);
                                                                            }}

                                                                            className={styles["parameter_radio_field"]}
                                                                        />
                                                                    </Flex>
                                                                    <div >
                                                                        {parameter.type === 'pair' ? (
                                                                            <Flex>

                                                                                <TooltipTop title="Click to select and copy">
                                                                                    <Text
                                                                                        className={styles.copyTextLabel}
                                                                                        onClick={() => handleCopyText(`change_parameter${index + 1}_start`)}
                                                                                    >
                                                                                        {`change_parameter${index + 1}_start`}
                                                                                    </Text>
                                                                                </TooltipTop>

                                                                                <TooltipTop title="Click to select and copy">
                                                                                    <Text
                                                                                        className={styles.copyTextLabel}
                                                                                        onClick={() => handleCopyText(`change_parameter${index + 1}_end`)}
                                                                                    >
                                                                                        {`change_parameter${index + 1}_end`}
                                                                                    </Text>
                                                                                </TooltipTop>
                                                                            </Flex>
                                                                        ) : (
                                                                            <TooltipTop title="Click to select and copy">
                                                                                <Text
                                                                                    className={styles.copyTextLabel}
                                                                                    onClick={() => handleCopyText(`change_parameter${index + 1}`)}
                                                                                >
                                                                                    {`change_parameter${index + 1}`}
                                                                                </Text>
                                                                            </TooltipTop>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {parameters.length > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                className={styles.removeColumnButton}
                                                                                onClick={() => removeColumn(parameter.id)}
                                                                                title={`Remove parameter ${index + 1}`}

                                                                            >
                                                                                <CrossIcon />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div style={{ marginBottom: '16px' }}>
                                                            <Text fontWeight={600} className="inputfield__label">Parameter Values</Text>
                                                            <Flex alignItems="center" gap="8px" className={styles["info_note"]}>
                                                                <InformativeIcon />
                                                                <Text fontSize={12}>Start and End Time fields will update after the scheduled run</Text>
                                                            </Flex>
                                                        </div>
                                                        <div className={styles.columnFieldsContainer}>
                                                            <div className={styles.gridTableHeaderParameteValues}>
                                                                <Text>Change Parameter</Text>
                                                                <Text textAlign="center">Initial Value</Text>
                                                                <Text textAlign="center">Increment Value</Text>
                                                            </div>
                                                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                {parameters?.map((parameter, index) => {
                                                                    if (parameter.type === 'pair') {
                                                                        return (
                                                                            <div key={parameter.id}>
                                                                                <div className={styles.gridParameterTableItems}>
                                                                                    <Text fontSize={14}>{`change_parameter${index + 1}_start`}</Text>
                                                                                    <div style={{ padding: '0 8px' }}>
                                                                                        <InputField
                                                                                            name={`parameterValues.${index}.start.initial`}
                                                                                            placeholder=""
                                                                                            autoComplete="off"
                                                                                            style={{ width: '100%' }}
                                                                                        />
                                                                                    </div>
                                                                                    <Text fontSize={14} textAlign="center">
                                                                                        (change_parameter{index + 1}_end - change_parameter{index + 1}_start)
                                                                                    </Text>
                                                                                </div>
                                                                                <div className={styles.gridParameterTableItems}>
                                                                                    <Text fontSize={14}>{`change_parameter${index + 1}_end`}</Text>
                                                                                    <div style={{ padding: '0 8px' }}>
                                                                                        <InputField
                                                                                            name={`parameterValues.${index}.end.initial`}
                                                                                            placeholder=""
                                                                                            autoComplete="off"
                                                                                            style={{ width: '100%' }}
                                                                                        />
                                                                                    </div>
                                                                                    <Text fontSize={14} textAlign="center">
                                                                                        (change_parameter{index + 1}_end - change_parameter{index + 1}_start)
                                                                                    </Text>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <div key={parameter.id} className={styles.gridParameterTableItems}>
                                                                                <Text fontSize={14}>{`change_parameter${index + 1}`}</Text>
                                                                                <div style={{ padding: '0 8px' }}>
                                                                                    <TooltipTop title="Only numerical values allowed">
                                                                                        <InputField
                                                                                            name={`parameterValues.${index}.initial`}
                                                                                            placeholder=""
                                                                                            autoComplete="off"
                                                                                            style={{ width: '100%' }}
                                                                                        />
                                                                                    </TooltipTop>
                                                                                </div>
                                                                                <div style={{ padding: '0 8px' }}>
                                                                                    <TooltipTop title="Only integer values allowed">
                                                                                        <InputField
                                                                                            name={`parameterValues.${index}.increment`}
                                                                                            placeholder="1"
                                                                                            type="number"
                                                                                            step={1}
                                                                                            pattern="-?[0-9]*"
                                                                                            onKeyDown={(e) => {
                                                                                                if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            autoComplete="off"
                                                                                            style={{ width: '100%' }}
                                                                                        />
                                                                                    </TooltipTop>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                })}
                                                            </div>

                                                        </div>
                                                    </div>
                                                </ShowWhenTrue>
                                            </div>

                                        </ShowWhenTrue>
                                    </div>
                                </ShowWhenTrue>
                            </div>
                            <div className={styles.hierarchyFormFooter}>
                                <ShowWhenTrue show={isAspenIp21View || isRDBMSview}>
                                    <Text fontSize={14} color="#aaa" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        Before continuing, verify your query runs successfully and all required parameters are filled in.
                                    </Text>
                                </ShowWhenTrue>
                                <button
                                    type="button"
                                    className="btn btn-grey btn-md"
                                    onClick={() => {
                                        setActiveStep(0);
                                        setShowForm(false);
                                    }}
                                >
                                    Back
                                </button>
                                <button type="submit" className="btn btn-yellow btn-md">
                                    Next
                                </button>
                            </div>
                        </>
                    )
                }}
            </Form>
            <div>
                <Modal
                    title={"Query"}
                    visible={isVisibleModal}
                    width={'80vw'}
                    onOk={() => setIsVisibleModal(false)}
                    okText="Apply"
                    onCancel={() => setIsVisibleModal(false)}
                    bodyStyle={{ height: '650px', overflow: 'auto', paddingBottom: '5px' }}
                    okButtonProps={{
                        style: {
                            backgroundColor: '#fcad01',
                            boxShadow: '0 1px 4px 0 rgba(252, 173, 1, 0.72)',
                            color: '#262231'
                        }
                    }}
                    cancelButtonProps={{
                        type: 'link',
                        style: {
                            color: 'white'
                        }
                    }}

                    className="editorTab"
                >
                    <div>
                        {!isEmpty(parameters) && <Text fontSize={14} color="#aaa">
                            You can copy-paste parameters below. Click a chip to copy the placeholder with curly braces, e.g. {`{change_parameter1}`}
                        </Text>}
                        <div className={styles.parametersModalContainer}>
                            {parameters?.map((parameter, index) => (
                                parameter?.type === 'pair' ? (
                                    <div key={parameter.id} className={styles.parameter_item_field}>
                                        {['start', 'end'].map((suffix) => {
                                            const label = `change_parameter${index + 1}_${suffix}`;
                                            return (
                                                <span
                                                    key={suffix}
                                                    className={styles.copyTextModalLabel}
                                                    onClick={() =>
                                                        navigator.clipboard
                                                            .writeText(`{${label}}`)
                                                            .then(() => successAlert('Copied To Clipboard'))
                                                            .catch(() => errorAlert('Failed To Copy Text'))
                                                    }
                                                >
                                                    {label}
                                                </span>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    (() => {
                                        const label = `change_parameter${index + 1}`;
                                        return (
                                            <span
                                                key={parameter.id}
                                                className={styles.copyTextModalLabel}
                                                onClick={() =>
                                                    navigator.clipboard
                                                        .writeText(`{${label}}`)
                                                        .then(() => successAlert('Copied To Clipboard'))
                                                        .catch(() => errorAlert('Failed To Copy Text'))
                                                }
                                            >
                                                {label}
                                            </span>
                                        );
                                    })()
                                )
                            ))}
                        </div>
                        <CustomMonacoEditor
                            {...editorControllerPropsIp21Aspen}
                            editorHeight="590px"
                            editorWidth="100%"
                            scrollBeyondLastLine
                            toolbarOptions={[
                                "Copy", "Clear", "Format"
                            ]}
                            language="sql"
                        />
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default HierarchyReqForm;
